/**
 * Price / currency formatting helpers for F-003.
 *
 * Prices are stored as `integer` cents (agorot) per design §3.1.
 * The UI shows decimal ILS via Intl.NumberFormat with a locale-specific
 * currency format and European (Latin) numerals in all locales — see
 * design §15 for the rationale.
 *
 * Parsing for form input is locale-aware (§15 D6): en/he accept ASCII
 * digits + ASCII dot; ar additionally normalizes U+066B Arabic decimal
 * comma to `.`. Arabic-Indic digits are REJECTED at V0 to avoid
 * silent-miscounting bugs across Eastern/Western Arabic numeral
 * conventions.
 */

import type { SupportedLocale } from '../i18n/direction';

/** Map app locales to BCP-47 locale tags used by Intl. Inlined here
 *  rather than imported from i18n internals so `format.ts` has zero
 *  runtime dependencies on i18next. */
const LOCALE_TO_BCP47: Record<SupportedLocale, string> = {
  ar: 'ar-IL',
  he: 'he-IL',
  en: 'en-IL',
};

/**
 * Format an integer cents amount as a localized ILS currency string.
 *
 * Uses `numberingSystem: 'latn'` in every locale so the price always
 * reads as "19.99 ₪" / "₪19.99" / "₪19.99" — per Requirements §11
 * architect note, Dabburiya merchants are fluent in Latin-digit prices
 * from real-world commerce, and forcing Eastern Arabic numerals here
 * would actively confuse the majority audience.
 *
 * @param cents Integer agorot (1 ILS = 100 agorot)
 * @param locale One of the three supported app locales
 * @returns Formatted string like "₪19.99" / "19.99 ₪" depending on locale
 */
export function formatPrice(cents: number, locale: SupportedLocale): string {
  const bcp47 = LOCALE_TO_BCP47[locale];
  const formatter = new Intl.NumberFormat(bcp47, {
    style: 'currency',
    currency: 'ILS',
    numberingSystem: 'latn',
  });
  return formatter.format(cents / 100);
}

/**
 * Convert an integer cents amount to the number shown in the form
 * (two decimal places, e.g. 1999 → 19.99). Used by the edit form's
 * "prefill from existing product" path.
 *
 * Returns NaN when cents is NaN or non-finite so callers surface a
 * clear validation error rather than writing garbage.
 */
export function centsToDisplay(cents: number): number {
  if (!Number.isFinite(cents)) return Number.NaN;
  return cents / 100;
}

/**
 * Convert a form-input decimal price to integer cents. Rounds via
 * `Math.round(display * 100)` because JavaScript float arithmetic
 * can drift by one ULP for two-decimal numbers.
 *
 * The caller is responsible for ensuring `display` is already a
 * sanitized number — this helper does not re-validate shape.
 */
export function displayToCents(display: number): number {
  if (!Number.isFinite(display)) return Number.NaN;
  return Math.round(display * 100);
}

/**
 * Locale-aware raw-string → decimal-number parser for the price input
 * (design §15 D6 contract). Returns NaN when the input is not a
 * recognizable decimal in the given locale.
 *
 *   en / he: ASCII digits `0-9` + ASCII dot `.` as decimal separator.
 *   ar:      same as en/he, PLUS Arabic decimal comma `٫` (U+066B)
 *            is normalized to `.` before parsing.
 *
 * REJECTED in every locale (returns NaN):
 *   - European decimal comma `,`
 *   - Full-width digits
 *   - Arabic-Indic digits (`٠-٩`)
 *   - Thousands separators (any form)
 *   - Whitespace inside the number
 *   - Empty string, non-numeric garbage
 *
 * The caller (Zod schema) rounds via `displayToCents` and enforces
 * two-decimal-place precision and the 10_000_000 upper bound.
 */
export function parseLocalePrice(raw: string, locale: SupportedLocale): number {
  if (typeof raw !== 'string') return Number.NaN;
  const trimmed = raw.trim();
  if (trimmed === '') return Number.NaN;

  // Reject any whitespace in the middle of the number outright.
  if (/\s/.test(trimmed)) return Number.NaN;

  // Locale-specific decimal-separator normalization.
  let normalized = trimmed;
  if (locale === 'ar') {
    // Accept the Arabic decimal comma U+066B as a synonym for `.`.
    normalized = normalized.replace(/\u066B/g, '.');
  }

  // After normalization, the string must contain ONLY ASCII digits and
  // at most one ASCII dot. This rejects Arabic-Indic digits (U+0660..U+0669),
  // full-width digits, European commas, thousands separators, and
  // locale-specific currency symbols.
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return Number.NaN;

  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : Number.NaN;
}
